import { DialogComponent } from '@theme/dialog';

/**
 * A custom element for the selected watch card and its variations drawer.
 *
 * The other in-stock watches of the same design are fetched once, after the
 * page has loaded, so the card can show how many there are. The drawer lists
 * them in pages for one size at a time, starting with the current watch's size.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} [dialog] - The drawer dialog.
 * @property {HTMLElement} [other] - The "Available on N other variations" line.
 * @property {HTMLElement} [sizeRow] - The "Size" row, hidden when the watches have no size.
 * @property {HTMLElement} [sizes] - The size buttons container.
 * @property {HTMLElement} [count] - The "N watches" counter.
 * @property {HTMLElement} [grid] - The watches grid.
 * @property {HTMLElement} [empty] - Shown when there are no other watches.
 * @property {HTMLElement} [more] - The footer with the "Show more variations" button.
 *
 * @extends {DialogComponent}
 */
class SelectedWatchComponent extends DialogComponent {
  // Watches without a design only render the heading, so there is no dialog
  requiredRefs = [];

  /** @type {HTMLElement[]} */
  #items = [];

  /** @type {Promise<void> | null} */
  #loading = null;

  /** @type {string | null} */
  #selectedSize = null;

  #visible = 0;

  connectedCallback() {
    super.connectedCallback();

    if (!this.dataset.url) return;

    if (document.readyState === 'complete') {
      this.#scheduleLoad();
    } else {
      window.addEventListener('load', () => this.#scheduleLoad(), { once: true });
    }
  }

  get #pageSize() {
    return Number(this.dataset.pageSize) || 8;
  }

  /**
   * Opens the drawer, loading the watches first if needed.
   */
  openDrawer = async () => {
    this.#visible = this.#pageSize;
    this.showDialog();

    try {
      await this.#load();
    } catch {
      // Fall through and show the empty state instead of a blank drawer
    }
    this.#renderSizes();
    this.#render();
  };

  /**
   * Shows the watches of the clicked size.
   *
   * `event.target` is the element carrying the `on:click` attribute, see
   * the note in size-guide.js.
   *
   * @param {Event} event - The click event.
   */
  selectSize = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    this.#selectedSize = target.dataset.size ?? null;
    this.#visible = this.#pageSize;
    this.#updatePressed();
    this.#render();
  };

  showMore = () => {
    this.#visible += this.#pageSize;
    this.#render();
  };

  #scheduleLoad() {
    const load = () => this.#load().catch(() => {});

    if ('requestIdleCallback' in window) {
      requestIdleCallback(load, { timeout: 2000 });
    } else {
      setTimeout(load, 200);
    }
  }

  /**
   * @returns {Promise<void>}
   */
  #load() {
    this.#loading ??= this.#fetchItems();
    return this.#loading;
  }

  async #fetchItems() {
    const { url, designId, productId } = this.dataset;
    if (!url) return;

    let html;
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Failed to load variations: ${response.status}`);
      html = await response.text();
    } catch (error) {
      // Let the next drawer open try again
      this.#loading = null;
      throw error;
    }

    const doc = new DOMParser().parseFromString(html, 'text/html');

    // Availability is only checked here (see sections/design-variations.liquid);
    // the design check keeps the list correct if the filter is ever removed in
    // Search & Discovery.
    this.#items = [...doc.querySelectorAll('.design-variations__item')].filter(
      (item) =>
        item instanceof HTMLElement &&
        item.dataset.designId === designId &&
        item.dataset.available === 'true' &&
        item.dataset.productId !== productId
    );

    this.#renderOther();
  }

  #renderOther() {
    const { other } = this.refs;
    if (!other) return;

    const total = this.#items.length;
    other.textContent = this.#format(total === 1 ? 'otherOne' : 'otherOther', total);
    other.hidden = total === 0;
  }

  #renderSizes() {
    const { sizes, sizeRow } = this.refs;
    if (!sizes) return;

    const values = /** @type {string[]} */ ([
      ...new Set(this.#items.map((item) => item.dataset.size).filter(Boolean)),
    ]);
    values.sort((a, b) => parseFloat(a) - parseFloat(b));

    // Start on the current watch's size, or the first one if it has no other watches
    const currentSize = this.dataset.size ?? '';
    this.#selectedSize = values.includes(currentSize) ? currentSize : values[0] ?? null;

    sizes.replaceChildren(
      ...values.map((size) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'selected-watch__size button-unstyled';
        button.dataset.size = size;
        button.textContent = size;
        button.setAttribute('on:click', '/selectSize');
        return button;
      })
    );

    if (sizeRow) sizeRow.hidden = values.length === 0;
    this.#updatePressed();
  }

  #updatePressed() {
    for (const button of this.refs.sizes?.querySelectorAll('.selected-watch__size') ?? []) {
      if (button instanceof HTMLElement) {
        button.setAttribute('aria-pressed', String(button.dataset.size === this.#selectedSize));
      }
    }
  }

  #render() {
    const { grid, count, more, empty } = this.refs;
    if (!grid) return;

    const matching = this.#selectedSize
      ? this.#items.filter((item) => item.dataset.size === this.#selectedSize)
      : this.#items;

    grid.replaceChildren(...matching.slice(0, this.#visible).map((item) => item.cloneNode(true)));

    if (count) {
      count.textContent = this.#format(matching.length === 1 ? 'countOne' : 'countOther', matching.length);
      count.hidden = matching.length === 0;
    }
    if (more) more.hidden = matching.length <= this.#visible;
    if (empty) empty.hidden = matching.length > 0;
  }

  /**
   * @param {'otherOne' | 'otherOther' | 'countOne' | 'countOther'} key - The data attribute holding the text.
   * @param {number} number - The number to put into the text.
   * @returns {string}
   */
  #format(key, number) {
    return (this.dataset[key] ?? '').replace('[number]', String(number));
  }
}

if (!customElements.get('selected-watch-component')) {
  customElements.define('selected-watch-component', SelectedWatchComponent);
}
